import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createAlert,
  deleteAlert,
  getLocationAlerts,
  getLocationHistory,
  updateAlert,
} from "../api/admin";
import { CustomResponse } from "../types/user";
import { AlertUpdateDto } from "../types/alert";

export function useCreateAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["create-alert"],
    mutationFn: createAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locationAlerts"] });
    },
    onError: (error) => {
      console.error("Error creating alert:", error);
    },
  });
}

export function useUpdateAlert(): UseMutationResult<
  CustomResponse<any>,
  Error,
  { data: AlertUpdateDto; alertId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["update-alert"],
    mutationFn: ({ data, alertId }) => updateAlert(data, alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locationAlerts"] });
    },
    onError: (error) => {
      console.error("Error updating alert:", error);
    },
  });
}

export function useDeleteAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["delete-alert"],
    mutationFn: deleteAlert,
    onSuccess: () => {
      // Invalidate all device parameters queries
      queryClient.invalidateQueries({ queryKey: ["locationAlerts"] });
    },
    onError: (error) => {
      console.error("Error deleting alert:", error);
    },
  });
}

export function useGetLocationAlerts(locationId: string) {
  return useQuery({
    queryKey: ["locationAlerts", locationId],
    queryFn: getLocationAlerts,
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 60 * 1000, // 60 seconds long polling
    refetchIntervalInBackground: true, // Continue polling when window is not focused
  });
}

export function useGetAlertHistories(
  locationId: string,
  fromDate: string,
  toDate: string,
) {
  return useQuery({
    queryKey: ["locationAlertHistories", locationId, fromDate, toDate],
    queryFn: getLocationHistory,
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 60 * 1000, // 60 seconds polling
    refetchIntervalInBackground: true, // Continue polling when window is not focused
  });
}
